/**
 * Product Attribute Dropdown Constants
 *
 * Predefined option lists for the Add/Edit Product form's single-select
 * dropdowns and multi-select fields (SV-390), plus the Storage Opened/Unopened
 * dropdowns (SV-81). Age of User, Region for Use, Protein Source, Protein
 * Hydrolysis, and CHO Source values come from the "Product Data Points.xlsx"
 * spreadsheet Paul Howard attached to the ticket (2026-09-28) — update this
 * file if that reference data changes.
 */
import type { DropdownOption } from './onboarding.constants';

export const TYPE_OF_PRODUCT_OPTIONS: DropdownOption[] = [
  { label: 'Enteral', value: 'Enteral' },
  { label: 'Parenteral', value: 'Parenteral' },
];

/** Used by the Gluten-Free / Halal / Kosher dropdowns. */
export const DIETARY_FLAG_OPTIONS: DropdownOption[] = [
  { label: 'No', value: 'No' },
  { label: 'Yes', value: 'Yes' },
];

export const AGE_FOR_USE_OPTIONS: DropdownOption[] = [
  { label: 'Adult', value: 'Adult' },
  { label: 'Pediatric < 1 yo', value: 'Pediatric < 1 yo' },
  { label: 'Pediatric ≥ 1 yo', value: 'Pediatric ≥ 1 yo' },
  { label: 'Preterm', value: 'Preterm' },
];

/** value is the region shortcode from the spreadsheet's "Shortcode" column. */
export const REGION_FOR_USE_OPTIONS: DropdownOption[] = [
  { label: 'Australia', value: 'AU' },
  { label: 'Canada', value: 'CA' },
  { label: 'Spain', value: 'ES' },
  { label: 'United Kingdom', value: 'UK' },
  { label: 'United States', value: 'US' },
];

export const PROTEIN_SOURCE_OPTIONS: DropdownOption[] = [
  '40 Casein', '60 Whey', 'AA', 'Acid Casein', 'Almond Butter', 'Beef',
  'Beta-Casein A2', 'Brown Rice', 'Buckwheat Flour', 'Buttermilk',
  'Calcium Caseinate', 'Calcium Caseinate (Milk)', 'Calcium-Potassium Caseinate',
  'Calcium-Potassium Caseinate (Milk)', 'Casein', 'Casein Glycomacropeptides',
  'Casein Hydrolysate', 'Caseinate', 'Chia Seeds', 'Chick Peas', 'Chicken',
  'Citric Acid Esters', 'Cocoa Processed With Alkali', 'Collagen', 'Corn Protein',
  'Cow Milk', 'Cow Milk Protein', 'Creatine Monohydrate', 'Crystalline AA',
  'Dairy-based Blend', 'Dehydrated Chicken Powder', 'Egg',
  'Enzymatically Hydrolyzed Casein', 'Enzymatically Hydrolyzed Whey Protein',
  'Enzymatically Hydrolyzed Whey Protein Isolate (From Milk)', 'Fava Bean',
  'Flaxseeds', 'Free AA', 'Galacto-oligosaccharides', 'Garbanzo Bean', 'Glycine',
  'Glycomacropeptide', 'GMP', 'Goat Milk', 'Goat Milk Powder', 'Goat Whey Powder',
  'Green Beans', 'Hemp Powder', 'Human Milk', 'Hydrolyzed Caseinate',
  'Hydrolyzed Pea Protein', 'Hydrolyzed Pea Protein Isolate',
  'Hydrolyzed Rice Protein', 'Hydrolyzed Sodium Caseinate', 'Hydrolyzed Whey',
  'Hydrolyzed Whey Isolate', 'Individual AA', 'L-AA', 'L-Arginine', 'L-Carnitine',
  'L-Cystine', 'Lab Derived AA', 'Lactalbumin', 'Milk', 'Milk Protein',
  'Milk Protein Concentrate', 'Milk Protein Isolate', 'Milk Solids', 'Millet',
  'Nonfat Milk', 'Oat Flour', 'Organic Brown Rice Flour',
  'Organic Cooked Dark Meat Chicken', 'Organic Hydrolyzed Pea Protein',
  'Organic Rice Protein', 'Organic Rice Protein Concentrate',
  'Organic Sweet Potato Puree', 'Organic Whole Milk',
  'Partially Hydrolyzed Milk Protein', 'Partially Hydrolyzed Nonfat Milk',
  'Partially Hydrolyzed Whey', 'Partially Hydrolyzed Whey Protein', 'Pea',
  'Pea Protein', 'Pea Protein Isolate', 'Plant Based Soy',
  'Pumpkin Seed Protein', 'Quinoa', 'Rice Flour', 'Rice Protein', 'Salmon',
  'Skimmed Goat Milk', 'Skimmed Milk', 'Skimmed Milk Powder',
  'Sodium Caseinate', 'Soy', 'Soy Protein', 'Soy Protein Isolate',
  'Sunflower seeds', 'Turkey', 'Vegetable Protein', 'Whey', 'Whey Concentrate',
  'Whey Isolate', 'Whey Powder', 'Whey Product', 'Whey Protein',
  'Whey Protein Concentrate', 'Whey Protein Hydrolysate', 'Whey Protein Isolate',
  'Whey Protein Isolate (Milk)', 'Whole Goat Milk', 'Whole Milk',
].map((v) => ({ label: v, value: v }));

export const PROTEIN_HYDROLYSIS_OPTIONS: DropdownOption[] = [
  'Crystalline AA', 'Extensive', 'Extensively', 'Extensively Hydrolyzed',
  'Free AA', 'Free AA & Intact', 'Hydrolyzed', 'Intact',
  'Intact With L-Methionine', 'L-AA & Intact', 'L-Carnitine Only', 'Partial',
  'Partial Hydrolysis', 'Partially Hydrolyzed',
].map((v) => ({ label: v, value: v }));

export const CHO_SOURCE_OPTIONS: DropdownOption[] = [
  'Agave', 'Agave Inulin', 'Agave Syrup', 'Allulose', 'Almond Butter', 'Apple',
  'Apple Juice', 'Banana', 'Beets', 'Black Currant Juice', 'Blackberries',
  'Blueberry', 'Brown Rice', 'Brown Rice Syrup', 'Brown Rice Syrup Solids',
  'Buckwheat Flour', 'Butternut Squash', 'Cane Sugar', 'Carob Bean Gum',
  'Carrot', 'Cellulose', 'Cellulose Gel', 'Cellulose Gel And Gum',
  'Cocoa Processed With Alkali', 'Corn', 'Corn Maltodextrin', 'Corn Starch',
  'Corn Syrup', 'Corn Syrup Solids', 'Cranberries', 'D-Galactose', 'D-Mannose',
  'Dextrin', 'Dextrose', 'Elderberry Juice', 'Erythritol',
  'Fructooligosaccharides', 'Fructose', 'Fruit and Vegetables',
  'Fruit Concentrate', 'Fruit Juice', 'Fruits', 'Fruits And Vegetables',
  'Galacto-oligosaccharides', 'Galactose', 'Glucose', 'Glucose Syrup',
  'Glucose Syrup Solids', 'Grape Juice', 'Green Beans', 'Guar Gum',
  'High Fructose Corn Syrup', 'Hydrolyzed Rice Starch',
  'Hydrolyzed Sodium Caseinate', 'Inulin', 'Isomaltose', 'Isomaltulose', 'Kale',
  'Lactose', 'Locust Bean Gum', 'Maltodextric', 'Maltodextrin', 'Maltose',
  'Mandarin Juice', 'Mango', 'Mango Puree', 'Mannitol', 'Modified Corn Starch',
  'Modified Food Starch', 'Modified Starch', 'Modified Tapioca Starch',
  'Monk Fruit', 'Monk Fruit Juice', 'Nonfat Milk', 'Oat Flour', 'Oats',
  'Orange Juice', 'Organic Brown Rice Flour', 'Partially Hydrolyzed Guar Gum',
  'Pea Starch', 'Peaches', 'Pear', 'Pectin', 'Pineapple Juice', 'Polydextrose',
  'Pomegranate Juice', 'Potassium Chloride', 'Potato', 'Potato Starch',
  'Pregelatinized Corn Starch', 'Prune', 'Pumpkin', 'Purple Potato',
  'Purple Sweet Potato', 'Quinoa', 'Red Beets', 'Red Bell Peppers',
  'Rice Bran Extract', 'Rice Concentrate', 'Rice Dextrin', 'Rice Flour',
  'Rice Starch', 'Rice Syrup Solids', 'Rolled Oat',
  'Short Chain Fructooligosaccharides', 'Skimmed Milk', 'Soy Lecithin',
  'Spinach', 'Squash', 'Starch', 'Stevia', 'Strawberry', 'Sucralose', 'Sucrose',
  'Sugar', 'Sweet Potato', 'Tapioca Dextrin', 'Tapioca Starch', 'Tapioca Syrup',
  'Tara Gum', 'Tare Gum', 'Tomato Paste', 'Vegetables', 'Wheat Maltodextrin',
  'Whey Protein Hydrolysate', 'Whole Grain Oat Flour', 'Xanthan Gum', 'Zucchini',
].map((v) => ({ label: v, value: v }));

/**
 * Used by the Storage Opened / Storage Unopened dropdowns. `value` is the
 * canonical string submitted to the API and must stay in English (existing
 * stored data already uses these values) — the UI renders a localized
 * `label` instead of this fallback English one; see PackagingStep.vue.
 */
export const STORAGE_LOCATION_OPTIONS: DropdownOption[] = [
  { label: 'Deep Freezer', value: 'Deep Freezer' },
  { label: 'Freezer', value: 'Freezer' },
  { label: 'Refrigerator', value: 'Refrigerator' },
  { label: 'Shelf', value: 'Shelf' },
];
